import { AiQuestion } from "../types/ai-question";
import { UserAnswer } from "../types/ai-question";
import { ChatMessage } from "../types/chat-message";
import axios from "axios";

export function getActiveQuestion({
  questions,
  currentQuestionIndex,
  focusedQuestionId,
}: {
  questions: AiQuestion[];
  currentQuestionIndex: number;
  focusedQuestionId: string | null;
}): AiQuestion | undefined {
  if (focusedQuestionId !== null) {
    const focusedQuestionNumber = parseInt(focusedQuestionId.replace("q", ""));
    return questions.find((q) => q.id === focusedQuestionNumber);
  }
  return questions[currentQuestionIndex];
}

export function questionRequiresFile(question?: AiQuestion): boolean {
  return !!question?.requiresFile;
}

export function isInputDisabled({
  question,
  isSubmitted,
  isTypingComplete,
  isQuestionAnswered,
  focusedQuestionId,
  isCompleted,
}: {
  question?: AiQuestion;
  isSubmitted: boolean;
  isTypingComplete: boolean;
  isQuestionAnswered: (questionId: string) => boolean;
  focusedQuestionId: string | null;
  isCompleted: boolean;
}): boolean {
  if (!question) return true;

  // Disable input for modal questions
  if (question.type === "modal") {
    return true;
  }

  // If we're completed, only allow input for repeated text questions
  if (isCompleted) {
    if (focusedQuestionId !== null && question.type === "text") {
      // Allow input for repeated text questions after completion
      return isSubmitted || !isTypingComplete;
    }
    // Disable input for all other cases when completed
    return true;
  }

  // Normal logic for non-completed state
  if (focusedQuestionId !== null && question) {
    return (
      isSubmitted ||
      !isTypingComplete ||
      (question.type === "select" && !isQuestionAnswered(`q${question.id}`))
    );
  }
  return (
    isSubmitted ||
    !isTypingComplete ||
    (question.type === "select" && !isQuestionAnswered(`q${question.id}`))
  );
}

// Extract question number from questionId (e.g., "q11" -> 11)
export function parseQuestionNumber(questionId: string): number {
  return parseInt(questionId.replace("q", ""));
}

// Find question by questionId
export function findQuestionById(
  questions: AiQuestion[],
  questionId: string
): AiQuestion | undefined {
  const questionNumber = parseQuestionNumber(questionId);
  return questions.find((q) => q.id === questionNumber);
}

// Update or add user answer
export function updateUserAnswer(
  prevAnswers: UserAnswer[],
  userAnswer: UserAnswer
): UserAnswer[] {
  const existingAnswerIndex = prevAnswers.findIndex(
    (ans) => ans.questionId === userAnswer.questionId
  );
  if (existingAnswerIndex >= 0) {
    // Replace existing answer
    const newAnswers = [...prevAnswers];
    newAnswers[existingAnswerIndex] = userAnswer;
    return newAnswers;
  } else {
    // Add new answer
    return [...prevAnswers, userAnswer];
  }
}

// Create user message for conversation
export function createUserMessage(message: string, attachment?: File | File[]) {
  return {
    id: `user-${Date.now()}`,
    authorName: "You",
    isUserMessage: true,
    messageContent: message,
    attachment,
  };
}

// Create AI question message for conversation
export function createAIQuestionMessage(question: AiQuestion) {
  return {
    id: `question-${question.id}`,
    authorName: "AI Assistant",
    isUserMessage: false,
    messageContent: question.question,
    questionId: `q${question.id}`,
  };
}

// Create repeat question message for conversation
export function createRepeatMessage(question: AiQuestion) {
  return {
    id: `repeat-${Date.now()}`,
    authorName: "AI Assistant",
    isUserMessage: false,
    messageContent: question.question,
    questionId: `q${question.id}`,
  };
}

// Create confirmation message for conversation
export function createConfirmationMessage(
  messageContent: string,
  questionId?: string
) {
  return {
    id: `confirmation-${Date.now()}`,
    authorName: "AI Assistant",
    isUserMessage: false,
    messageContent,
    questionId,
  };
}

// Create completion message for conversation
export function createCompletionMessage(messageContent: string) {
  return {
    id: `completion-${Date.now()}`,
    authorName: "AI Assistant",
    isUserMessage: false,
    messageContent,
  };
}

// Create submission message for conversation
export function createSubmissionMessage(messageContent: string) {
  return {
    id: `submission-${Date.now()}`,
    authorName: "AI Assistant",
    isUserMessage: false,
    messageContent,
  };
}

// Check if theres a next question and return it
export function getNextQuestion(
  questions: AiQuestion[],
  currentQuestionIndex: number
): { nextQuestion: AiQuestion; hasNext: boolean } {
  const nextQuestionIndex = currentQuestionIndex + 1;
  if (nextQuestionIndex < questions.length) {
    return { nextQuestion: questions[nextQuestionIndex], hasNext: true };
  }
  return { nextQuestion: questions[currentQuestionIndex], hasNext: false };
}

// Check if question is being repeated
export function isRepeatedQuestion(messageId?: string): boolean {
  return messageId?.startsWith("repeat-") || false;
}

// Check if message is a confirmation
export function isConfirmationMessage(messageId?: string): boolean {
  return messageId?.startsWith("confirmation-") || false;
}

// Extract payload from user answers for API
export function extractPayloadFromAnswers(
  answers: UserAnswer[],
  questions: AiQuestion[]
  // role: ROLES
): Record<string, any> {
  const payload: Record<string, any> = {};
  payload.machineLearningIds = [];
  payload.techniquesIds = [];
  payload.users = [];
  payload.algorithmsIds = [];
  // payload.role = role;
  payload.version = "1";
  payload.keyContactUserId = 0;
  answers.forEach((answer) => {
    const question = questions.find((q) => q.id === answer.questionId);
    if (!question) return;

    const fieldKey = question.field?.key;
    if (!fieldKey) return;

    if (question.type === "select") {
      if (question.field?.isMultiple) {
        // Multiple select - answer.answer is now an array of ids (strings or numbers)
        const answerValues = Array.isArray(answer.answer)
          ? answer.answer.map((val) => parseInt(val))
          : typeof answer.answer === "string" && answer.answer.length > 0
          ? [parseInt(answer.answer)]
          : [];
        payload[fieldKey] = answerValues;
      } else {
        // Single select - answer.answer is a string or array with one element
        if (Array.isArray(answer.answer)) {
          payload[fieldKey] =
            answer.answer.length > 0 ? parseInt(answer.answer[0]) : undefined;
        } else {
          payload[fieldKey] = parseInt(answer.answer);
        }
      }
    } else {
      // Text questions - use the answer as is
      payload[fieldKey] = answer.answer;
    }
  });

  console.log(payload);
  return payload;
}

// Extract attachments from user answers
export function extractAttachmentsFromAnswers(
  answers: UserAnswer[],
  questions: AiQuestion[]
): File[] {
  const attachments: File[] = [];

  answers.forEach((answer) => {
    const question = questions.find((q) => q.id === answer.questionId);
    if (!question) return;

    // Check if this question requires a file and has an attachment
    if (question.requiresFile && answer.attachment) {
      if (Array.isArray(answer.attachment)) {
        // Handle array of files
        attachments.push(...answer.attachment);
      } else {
        // Handle single file
        attachments.push(answer.attachment);
      }
    }
  });

  return attachments;
}

// Check if a message is a select AI question
export function isSelectAIQuestion(
  message: ChatMessage,
  questions: AiQuestion[],
  isTypingComplete: boolean,
  currentMessageId?: string | null
): boolean {
  if (message.isUserMessage || !message.questionId) {
    return false;
  }

  const questionNumber = parseInt(message.questionId.replace("q", ""));
  const question = questions.find((q) => q.id === questionNumber);

  return (
    question?.type === "select" &&
    (isTypingComplete || message.id !== (currentMessageId || undefined))
  );
}

// Get select question from message
export function getSelectQuestionFromMessage(
  message: ChatMessage,
  questions: AiQuestion[]
): AiQuestion | undefined {
  if (!message.questionId) return undefined;

  const questionNumber = parseInt(message.questionId.replace("q", ""));
  return questions.find((q) => q.id === questionNumber);
}

// Check if a message is disabled (for ChatBox)
export function isMessageDisabled(
  message: ChatMessage,
  focusedQuestionId: string | null,
  isQuestionBeingEdited: (questionId: string) => boolean,
  isUserMessageForFocusedQuestion: (
    message: any,
    focusedQuestionId: string
  ) => boolean
): boolean {
  return (
    focusedQuestionId !== null &&
    message.questionId !== focusedQuestionId &&
    !isQuestionBeingEdited(message.questionId!) &&
    !(
      message.isUserMessage &&
      focusedQuestionId &&
      isUserMessageForFocusedQuestion(message, focusedQuestionId)
    )
  );
}

// Check if a current message is focused (for ChatBox)
export function isMessageFocused(
  message: ChatMessage,
  focusedQuestionId: string | null
): boolean {
  return focusedQuestionId !== null && message.questionId === focusedQuestionId;
}

// Check if another message is focused (for ChatBox)
export function isAnotherMessageFocused(
  message: ChatMessage,
  focusedQuestionId: string | null
): boolean {
  return focusedQuestionId !== null && message.questionId !== focusedQuestionId;
}

// Check if a message is a repeated question (for ChatBox)
export function isMessageRepeatedQuestion(
  message: ChatMessage,
  focusedQuestionId: string | null
): boolean {
  // A message is a repeated question if it's an AI message with a questionId
  // that matches the focused question, and it's a repeat message
  return (
    !message.isUserMessage &&
    message.questionId === focusedQuestionId &&
    message.id.startsWith("repeat-")
  );
}

// Upload files to case
export async function uploadFiles(
  files: any[],
  caseId: number,
  internalId: string,
  caseServices: any
): Promise<void> {
  if (files.length && files.length <= 10) {
    files.forEach((file: any, index: number) => {
      caseServices
        .requestSignedUrl(
          file.name,
          file.type.substring(file.type.lastIndexOf("/") + 1),
          "files",
          internalId
        )
        .then((response: any) => {
          const { signedRequest, url } = response;
          const cancelTokenSource = axios.CancelToken.source();
          files[index].cancelTokenSource = cancelTokenSource;
          files[index].url = url;
          caseServices
            .uploadToSignedURL(
              signedRequest,
              files[index],
              cancelTokenSource.token,
              (event: any) => {}
            )
            .then(async (uploadResponse: any) => {
              if (uploadResponse === "canceled") {
                return;
              }
              const type = file.type.substring(file.type.lastIndexOf("/") + 1);
              const name = file.name.split("." + type)[0];
              const size = `${(file.size / 1024 / 1000).toFixed(2)} MB`;
              const payload = {
                caseId,
                name,
                type,
                size,
                url: file.url,
              };
              // push uploaded files to save in communication or notes tabs
              await caseServices.saveFileAndComment(payload, "", caseId);
            });
        });
    });
  }
}
